import {Component, ViewEncapsulation, Input, OnInit, OnDestroy, Output, EventEmitter, ChangeDetectionStrategy} from '@angular/core';
import { cloneDeep } from 'lodash-es';
import {OrderService} from '../../../services/order.service';
import {ConfigService} from '../../../services/config.service';
import {ModalService} from '../../../services/modal.service';
import {ToastrService} from 'ngx-toastr';
import {from, of, Subject} from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, takeWhile } from 'rxjs/operators';
import {Store} from '@ngrx/store';
import {IAppState} from '../../../store/app.store';
import {selectInternalOrderCodes} from '../../../store/selectors/internal-order.selectors';
import {Order} from '../../../models/order.model';
import {OrderItem} from '../../../models/order-item.model';
import {OrderItemDelivery} from '../../../models/order-item-delivery.model';
import {EXTRA_INTERNAL_ORDER_LENGTH, INTERNAL_ORDER_LENGTH} from '../../../constants/globals';
import {Features} from '../../../interfaces/features';
import {validateDeliveryDuplicates, wbsIOSymbolsAllowed} from '../../../shared/helpers';

@Component({
    selector: 'app-internal-order-code',
    templateUrl: './internal-order-code.component.html',
    styleUrls: ['./internal-order-code.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class InternalOrderCodeComponent implements OnInit, OnDestroy {

  searchSubject: Subject<string> = new Subject<string>();
  alive = true;
  filteredCodes: string[]  = [];
  selectedCode: string;
  inProcess = false;
  wasClearedDueToDisabled = false;

  @Input() disabled = false;
  @Input() order: Order;
  @Input() orderItem: OrderItem;
  @Input() delivery: OrderItemDelivery;
  @Input() shouldUpdateOrder  =  false;
  @Input() readonly = false;
  @Input() showWithModalDialog = false;

  @Input() class = '';
  @Output() changed =  new EventEmitter<void>();

  constructor(private configService: ConfigService,
              private toastr: ToastrService,
              private orderService: OrderService,
              private modalService: ModalService,
              private store: Store<IAppState>) { }


  ngOnInit() {

    // Load the codes on demand when the billing-code dropdown is actually rendered,
    // instead of eagerly at app startup. Idempotent, so multiple deliveries share one request.
    if (this.showBillingCodeDropdown && !this.readonly) {
      this.orderService.getInternalOrderCodes();
    }

    this.store.select(selectInternalOrderCodes)
      .pipe(takeWhile(() => this.alive))
      .subscribe((codes) => {
        this.internalCodes = codes;
        this.filteredCodes = [...codes];
        this.selectedCode = this.delivery?.internalOrder || null;
        this.clearDisabledSelection(codes);
      });

    this.selectedCode = this.delivery?.internalOrder || null;

    this.searchSubject.pipe(
      takeWhile( () => this.alive),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(term => {
        if (!term) {
          return of(this.internalCodes);
        }
        const termLowerCase = term.toLowerCase();
        const foundCodes  =  this.internalCodes.filter( code => code.toLowerCase().indexOf(termLowerCase) > -1);
        return of(foundCodes);
      })
    ).subscribe( (retCodes: string[]) => {
      this.filteredCodes = retCodes;
    });
  }


  ngOnDestroy() {
    this.alive = false;
  }

  /**
   * If the currently selected billing code is no longer part of the active
   * billing codes list, clear the selection so the dropdown is reset and
   * surface an info icon explaining why the value was cleared.
   */
  private clearDisabledSelection(codes: string[]): void {
    // Only relevant for the billing code dropdown feature.
    if (!this.showBillingCodeDropdown) {
      return;
    }

    const currentValue = this.delivery?.internalOrder;

    // Do not clear while the code list is still loading (empty list).
    if (!currentValue || !codes || codes.length === 0) {
      return;
    }

    const stillActive = codes.some((code: string) => code === currentValue);
    if (stillActive) {
      return;
    }

    // The previously selected code was disabled in Project Manager.
    this.delivery.wbs = '';
    this.delivery.internal_order = '';
    this.selectedCode = null;
    this.wasClearedDueToDisabled = true;
    this.orderItem.updateDelivery(this.delivery);
    this.changed.emit();
  }


  get showInternalOrderCodes(): boolean {
    return this.configService.features.showInternalOrderCodes;
  }

  get showBillingCodeDropdown(): boolean {
    return this.configService.fedInfoFeature.showBillingCodeDropdown;
  }

  internalCodes: string[] = [];

  onInternalOrderChanged(newValue: string) {
    const value  =  newValue ? newValue.trim() : '';
    if (this.order.isWBSDefined) {
      this.delivery.wbs = value;
    } else {
      this.delivery.internal_order = value;
    }
    this.orderItem.updateDelivery(this.delivery);
    if (this.shouldUpdateOrder) {
      this.saveInternalOrder();
    }

    this.changed.emit();
  }

  onInternalOrderReset() {
    this.delivery.wbs = '';
    this.delivery.internal_order = '';
    this.orderItem.updateDelivery(this.delivery);
  }

  onSelect(data) {
    if (data) {
      this.selectedCode = data;
      this.wasClearedDueToDisabled = false;
      this.onInternalOrderChanged(data);
    }
  }

  private saveInternalOrder() {
    const  updateOrderItemDelivery = cloneDeep(this.delivery);

    if (updateOrderItemDelivery.quantity > 0 && !updateOrderItemDelivery.internalOrder) {
      const warningMsg = `${this.internalOrderLabel} is required`;
      this.toastr.warning(warningMsg, 'Warning');
      return;
    }


    if (this.features.showInternalOrderCodes) {
      // validate length of wbd and internal order
      if (updateOrderItemDelivery.internalOrder.length > EXTRA_INTERNAL_ORDER_LENGTH) {
        this.toastr.warning(`Internal Order should not exceed ${EXTRA_INTERNAL_ORDER_LENGTH} symbols.`, 'Warning');
        return;
      }

    } else {
      if (this.configService.fedInfoFeature.showWbs) {
        // validate length of wbd and internal order
        if (updateOrderItemDelivery.internalOrder && updateOrderItemDelivery.internalOrder !== '0') {
          if (updateOrderItemDelivery.internalOrder.length > INTERNAL_ORDER_LENGTH) {
            this.toastr.warning(`Internal Order should not exceed ${INTERNAL_ORDER_LENGTH} symbols.`, 'Warning');
            return;
          }
          if (!wbsIOSymbolsAllowed(updateOrderItemDelivery.internalOrder)) {
            const ioLabel = this.order?.isWBSDefined ? 'WBS' : 'Internal Order';
            this.toastr.warning(`${ioLabel} allows  only digits, letters, /, #, -, . and space`);
            return;
          }
        }
      }
    }

    const duplicateError = validateDeliveryDuplicates(this.order, this.orderItem, this.configService.wbsValidationOptions);
    if (duplicateError) {
      this.toastr.warning(duplicateError, 'Warning');
      return;
    }


    this.inProcess = true;
    this.orderService.updateOrderItem(this.order, this.orderItem)
        .subscribe(data => {
          this.inProcess = false;
          if (data) {
            this.toastr.success(`${this.internalOrderLabel} has been updated.`);
          }
        });
  }

  get features(): Features {
    return this.configService.features;
  }

  get internalOrderLabel(): string {
      return this.configService.getInternalOrderLabel(this.order?.isWBSDefined, this.order?.isNotReal);
    }

  showIOEditModal() {
    from(this.modalService.showInternalOrderModal(this.order, this.orderItem, this.delivery))
        .subscribe( result => {
          if (result) {
            if (this.order.isWBSDefined) {
              this.delivery.wbs = result.internalOrder;
            } else {
              this.delivery.internal_order = result.internalOrder;
            }
          }
        })
  }

  get defaultInternalOrder(): string {
    return this.delivery.internalOrder || this.order.internalOrder;
  }

  get isItemCut(): boolean {
    return this?.orderItem?.item_cut === true;
  }

  get isDisabled(): boolean {
    return this.disabled || this.isItemCut;
  }
}
